import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, Lock, Activity, Gauge, FileWarning } from "lucide-react";

const layers = [
  { icon: KeyRound, label: "Row Level Security", desc: "Per-tenant data isolation at the database layer." },
  { icon: Lock, label: "Role-based access", desc: "Granular permissions, scoped to action and object." },
  { icon: ShieldCheck, label: "Hardened CSP", desc: "Strict content security headers by default." },
  { icon: Activity, label: "Session timeout", desc: "Continuous session integrity monitoring." },
  { icon: Gauge, label: "Rate limiting", desc: "Anti-abuse and DoS controls at the edge." },
  { icon: FileWarning, label: "CSV injection protection", desc: "Safe-by-design imports and exports." },
];

export default function Security() {
  return (
    <section
      id="security"
      data-testid="security-section"
      className="relative py-16 sm:py-24 lg:py-40 overflow-hidden bg-[#040A0A] border-y border-[#0E2020]"
    >
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, #4A6E6E 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060C0C] via-transparent to-[#060C0C]" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
        <div className="max-w-3xl mb-10 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3"
          >
            Security
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] font-medium mb-5"
          >
            Security built into <span className="italic font-normal text-[#9BB8B8]">every</span> layer.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#9BB8B8] text-lg max-w-xl"
          >
            Designed for organizations that can&apos;t afford to be wrong. Every layer is hardened by default.
          </motion.p>
        </div>

        {/* Architecture diagram */}
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-5">
            <ArchDiagram />
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {layers.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`security-layer-${i}`}
                  className="rounded-xl p-4 border border-[#D4EDE0] hover:border-[#7ACCA0] transition-colors relative overflow-hidden group"
                  style={{ background: "#F0FFF0" }}
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <l.icon className="w-4 h-4 text-[#FF4F00]" />
                    <div className="text-sm font-semibold text-[#0D1117]">{l.label}</div>
                  </div>
                  <div className="text-xs text-[#374151] leading-relaxed">{l.desc}</div>
                </motion.div>
              ))}
            </div>

            {/* Badges */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="security-badges">
              {["Strict TypeScript", "RLS Protected", "Enterprise Ready"].map((b) => (
                <span
                  key={b}
                  className="badge-lightning px-3 py-1.5 rounded-full text-[11px] font-mono tracking-wide text-[#86EFAC] border border-[#16A34A] bg-[#052E16]"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ArchDiagram = () => {
  const cardRef = useRef(null);
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, visible: false });

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setSpotlight({ x, y, visible: true });
  };

  return (
  <div
    ref={cardRef}
    onMouseMove={handleMouseMove}
    onMouseLeave={() => setSpotlight(s => ({ ...s, visible: false }))}
    className="rounded-2xl p-5 h-full relative overflow-hidden border border-[#E8E8E4] cursor-default"
    style={{ background: "#FFFAFA" }}
    data-testid="arch-diagram"
  >
    {/* Mouse spotlight overlay */}
    <div
      className="absolute inset-0 pointer-events-none rounded-2xl transition-opacity duration-300"
      style={{
        opacity: spotlight.visible ? 1 : 0,
        background: `radial-gradient(circle 180px at ${spotlight.x}% ${spotlight.y}%, rgba(255,79,0,0.08) 0%, transparent 70%)`,
      }}
    />

    <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-4">Architecture</div>

    <ArchNode label="Frontend" sub="React · TypeScript · Vite" tone="white" />
    <Connector />
    <ArchNode label="Security Layer" sub="RLS · RBAC · CSP · Rate-limit" tone="accent" highlight />
    <Connector />
    <ArchNode label="Backend" sub="Supabase · Postgres" tone="muted" />

    <div className="absolute right-4 top-4 text-[9px] font-mono text-[#16A34A] flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] pulse-dot" /> SECURED
    </div>
  </div>
  );
};

const ArchNode = ({ label, sub, tone, highlight }) => {
  const styles = {
    white: "bg-white border-[#E5E5E7] shadow-sm",
    accent: "bg-[#FF4F00]/8 border-[#FF4F00]/30 shadow-sm",
    muted: "bg-[#F5F5F4] border-[#E0E0DC] shadow-sm",
  }[tone];
  return (
    <div className={`relative rounded-xl border p-3.5 ${styles}`}>
      {highlight && (
        <div className="absolute inset-0 rounded-xl border border-[#FF4F00]/20 animate-pulse pointer-events-none" />
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-[#0A0A0B] font-medium">{label}</div>
          <div className="text-[11px] text-[#52525B] font-mono mt-0.5">{sub}</div>
        </div>
        <div className="text-[9px] font-mono text-[#6B7280] bg-[#F0F0EE] border border-[#E2E2DE] rounded px-1.5 py-0.5">LAYER</div>
      </div>
    </div>
  );
};

const Connector = () => (
  <svg className="block mx-auto my-1.5" width="2" height="22" viewBox="0 0 2 22" aria-hidden>
    <line x1="1" y1="0" x2="1" y2="22" stroke="#C8C8C4" strokeDasharray="3 3" className="animated-dash" />
  </svg>
);
